import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { config } from "../../../../../Helpers/env";

const USE_MOCK_DATA = false; 


export const CLIENT_TYPES = [
    { value: "new_acceptor", label: "New Acceptor" },
    { value: "revisit", label: "Revisit / Continuing" },
    { value: "restart", label: "Restart (previously discontinued)" },
    { value: "referral", label: "Referral from another facility" },
];

export const FP_METHODS = [
    { value: "coc", label: "Combined Oral Contraceptives (COC)", category: "short_acting", renewalMonths: null, requiresBP: true },
    { value: "pop", label: "Progestin-Only Pills (POP)", category: "short_acting", renewalMonths: null, requiresBP: false },
    { value: "dmpa", label: "DMPA Injectable (Depo-Provera)", category: "short_acting", renewalMonths: 3, requiresBP: true },
    { value: "net_en", label: "NET-EN Injectable (Noristerat)", category: "short_acting", renewalMonths: 2, requiresBP: true },
    { value: "jadelle", label: "Jadelle Implant (5 years)", category: "larc", renewalMonths: 60, requiresBP: true },
    { value: "implanon", label: "Implanon NXT Implant (3 years)", category: "larc", renewalMonths: 36, requiresBP: true },
    { value: "copper_iud", label: "Copper IUD (10–12 years)", category: "larc", renewalMonths: 120, requiresBP: false },
    { value: "lng_ius", label: "LNG-IUS (Mirena, 5 years)", category: "larc", renewalMonths: 60, requiresBP: false },
    { value: "male_condom", label: "Male Condom", category: "barrier", renewalMonths: null, requiresBP: false },
    { value: "female_condom", label: "Female Condom", category: "barrier", renewalMonths: null, requiresBP: false },
    { value: "tubal_ligation", label: "Tubal Ligation (BTL)", category: "permanent", renewalMonths: null, requiresBP: false },
    { value: "vasectomy", label: "Vasectomy", category: "permanent", renewalMonths: null, requiresBP: false },
    { value: "lam", label: "LAM (Lactational Amenorrhea)", category: "natural", renewalMonths: 6, requiresBP: false },
    { value: "sdm", label: "Standard Days Method (SDM)", category: "natural", renewalMonths: null, requiresBP: false },
    { value: "emergency_contraception", label: "Emergency Contraception (EC)", category: "emergency", renewalMonths: null, requiresBP: false },
];

export const FP_METHOD_CATEGORIES = [
    { value: "short_acting", label: "Short-Acting Hormonal", icon: "💊", color: "#1565c0" },
    { value: "larc", label: "Long-Acting Reversible (LARC)", icon: "🛡️", color: "#2e7d32" },
    { value: "barrier", label: "Barrier Methods", icon: "🔒", color: "#6a1b9a" },
    { value: "permanent", label: "Permanent Methods", icon: "✂️", color: "#c62828" },
    { value: "natural", label: "Natural / Fertility-Based", icon: "🌿", color: "#00695c" },
    { value: "emergency", label: "Emergency Contraception", icon: "⚡", color: "#e65100" },
];

export const SIDE_EFFECT_OPTIONS = [
    { value: "irregular_bleeding", label: "Irregular Bleeding / Spotting" },
    { value: "amenorrhea", label: "Amenorrhea (no periods)" },
    { value: "heavy_bleeding", label: "Heavy or Prolonged Bleeding" },
    { value: "headache", label: "Headache" },
    { value: "weight_gain", label: "Weight Gain" },
    { value: "weight_loss", label: "Weight Loss" },
    { value: "nausea", label: "Nausea" },
    { value: "breast_tenderness", label: "Breast Tenderness" },
    { value: "mood_changes", label: "Mood Changes / Depression" },
    { value: "decreased_libido", label: "Decreased Libido" },
    { value: "acne", label: "Acne" },
    { value: "hair_loss", label: "Hair Loss" },
    { value: "abdominal_pain", label: "Abdominal / Pelvic Pain" },
    { value: "vaginal_discharge", label: "Abnormal Vaginal Discharge" },
    { value: "insertion_site_pain", label: "Insertion Site Pain (implant/IUD)" },
    { value: "expulsion", label: "IUD Expulsion" },
    { value: "none", label: "No Side Effects" },
];

export const DISCONTINUATION_REASONS = [
    { value: "side_effects", label: "Side Effects" },
    { value: "desire_pregnancy", label: "Desire for Pregnancy" },
    { value: "method_failure", label: "Method Failure (Pregnancy)" },
    { value: "partner_opposition", label: "Partner Opposition" },
    { value: "access_issues", label: "Access / Availability Issues" },
    { value: "medical_reason", label: "Medical Reason / Contraindication" },
    { value: "expiry", label: "Method Expired (LARC removal due)" },
    { value: "personal_choice", label: "Personal Choice / Other" },
];

export const FP_STATUS_OPTIONS = [
    { value: "active", label: "Active", color: "#2e7d32" },
    { value: "discontinued", label: "Discontinued", color: "#c62828" },
    { value: "switched", label: "Switched to another method", color: "#f57f17" },
    { value: "expired", label: "Expired / Due for renewal", color: "#e65100" },
];


export const MEC_CATEGORIES = {
    coc: {
        contraindications: [
            "Breastfeeding < 6 weeks postpartum",
            "Smoker age ≥ 35 (≥15 cigarettes/day)",
            "Hypertension (BP ≥ 160/100)",
            "History of DVT/PE",
            "Migraine with aura",
            "Current breast cancer",
            "Diabetes with vascular disease",
            "Liver disease (active)",
        ],
        precautions: [
            "Breastfeeding 6 weeks–6 months postpartum",
            "Age ≥ 35 and light smoker",
            "BP 140-159/90-99",
            "History of migraine without aura, age ≥ 35",
            "Gallbladder disease",
        ],
    },
    pop: {
        contraindications: [
            "Current breast cancer",
        ],
        precautions: [
            "Breastfeeding < 6 weeks postpartum",
            "History of breast cancer (> 5 years ago)",
            "Liver tumors or active liver disease",
            "Taking anti-epileptic or rifampicin",
        ],
    },
    dmpa: {
        contraindications: [
            "Current breast cancer",
            "Unexplained vaginal bleeding",
        ],
        precautions: [
            "Hypertension (BP ≥ 160/100)",
            "History of DVT/PE on anticoagulation",
            "History of breast cancer > 5 years ago",
            "Diabetes with vascular disease",
        ],
    },
    net_en: {
        contraindications: [
            "Current breast cancer",
            "Unexplained vaginal bleeding",
        ],
        precautions: [
            "Hypertension (BP ≥ 160/100)",
            "History of DVT/PE",
        ],
    },
    jadelle: {
        contraindications: [
            "Current breast cancer",
            "Unexplained vaginal bleeding",
            "Active liver disease or liver tumors",
        ],
        precautions: [
            "Taking anti-epileptic medications or rifampicin",
            "History of breast cancer > 5 years ago",
        ],
    },
    implanon: {
        contraindications: [
            "Current breast cancer",
            "Unexplained vaginal bleeding",
            "Active liver disease or liver tumors",
        ],
        precautions: [
            "Taking anti-epileptic medications or rifampicin",
            "History of breast cancer > 5 years ago",
        ],
    },
    copper_iud: {
        contraindications: [
            "Current PID or purulent cervicitis",
            "Unexplained vaginal bleeding",
            "Cervical cancer (awaiting treatment)",
            "Uterine anomaly distorting the cavity",
            "Current pregnancy",
            "Postpartum/post-abortion sepsis",
        ],
        precautions: [
            "Increased STI risk",
            "48 hours–4 weeks postpartum",
            "Severe anemia",
            "Wilson disease",
        ],
    },
    lng_ius: {
        contraindications: [
            "Current PID or purulent cervicitis",
            "Unexplained vaginal bleeding",
            "Current breast cancer",
            "Cervical cancer (awaiting treatment)",
            "Uterine anomaly distorting the cavity",
            "Current pregnancy",
        ],
        precautions: [
            "Increased STI risk",
            "48 hours–4 weeks postpartum",
            "History of breast cancer > 5 years ago",
        ],
    },
};

/* ─── Helpers ─── */
const todayDate = function () {
    const d = new Date();
    const pad = function (n) { return n < 10 ? "0" + n : "" + n; };
    return d.getFullYear() + "-" + pad(d.getMonth() + 1) + "-" + pad(d.getDate());
};

const calculateRenewalDate = function (provisionDate, method) {
    if (!provisionDate || !method) return "";
    const methodInfo = FP_METHODS.find(function (m) { return m.value === method; });
    if (!methodInfo || !methodInfo.renewalMonths) return "";
    const d = new Date(provisionDate);
    d.setMonth(d.getMonth() + methodInfo.renewalMonths);
    const pad = function (n) { return n < 10 ? "0" + n : "" + n; };
    return d.getFullYear() + "-" + pad(d.getMonth() + 1) + "-" + pad(d.getDate());
};

const getMethodLabel = function (methodValue) {
    const m = FP_METHODS.find(function (f) { return f.value === methodValue; });
    return m ? m.label : methodValue;
};

const getMethodCategory = function (methodValue) {
    const m = FP_METHODS.find(function (f) { return f.value === methodValue; });
    return m ? m.category : "";
};

/* ─── Initial Form ─── */
const INITIAL_FP_FORM = {
    client_type: "",
    method_provided: "",
    provision_date: todayDate(),
    expected_renewal_date: "",
    quantity_provided: "",
    side_effects: [],
    side_effect_management: "",
    switched_from: "",
    switch_reason: "",
    discontinuation_reason: "",
    bp_systolic: "",
    bp_diastolic: "",
    weight_check: "",
    medical_eligibility_checked: false,
    contraindications_noted: [],
    counselling_notes: "",
    status: "active",
};

/* ═══ MOCK DATA ═══ */

const MOCK_FP_RECORDS = [
    {
        id: 5001,
        patient_number: "MOCK-PT-001",
        visit_id: "V-001",
        client_type: "new_acceptor",
        method_provided: "jadelle",
        provision_date: "2025-08-15",
        expected_renewal_date: "2030-08-15",
        quantity_provided: "",
        side_effects: ["irregular_bleeding"],
        side_effect_management: "Counselled on expected spotting in first 3 months. Reassured.",
        switched_from: "",
        switch_reason: "",
        discontinuation_reason: "",
        bp_systolic: 118,
        bp_diastolic: 72,
        weight_check: 62,
        medical_eligibility_checked: true,
        contraindications_noted: [],
        counselling_notes: "Client counselled on all available methods. Chose Jadelle for long-term protection. Informed about common side effects.",
        provided_by: "Dr. Sarah Nakato",
        status: "active",
        created_at: "2025-08-15T10:30:00",
    },
    {
        id: 5002,
        patient_number: "MOCK-PT-001",
        visit_id: "V-002",
        client_type: "revisit",
        method_provided: "jadelle",
        provision_date: "2025-11-20",
        expected_renewal_date: "2030-08-15",
        quantity_provided: "",
        side_effects: ["none"],
        side_effect_management: "",
        switched_from: "",
        switch_reason: "",
        discontinuation_reason: "",
        bp_systolic: 115,
        bp_diastolic: 70,
        weight_check: 63,
        medical_eligibility_checked: true,
        contraindications_noted: [],
        counselling_notes: "Follow-up visit. Implant in situ, no complications. Client satisfied with method.",
        provided_by: "Nurse Florence Akello",
        status: "active",
        created_at: "2025-11-20T14:00:00",
    },
];

/* ═══ HOOK ═══ */
export const useFamilyPlanning = function (actions, patient_number, visit_id) {

    /* ─── FP records ─── */
    const recordsState = useState([]);
    const fpRecords = recordsState[0];
    const setFpRecords = recordsState[1];

    /* ─── Form state ─── */
    const formState = useState(Object.assign({}, INITIAL_FP_FORM));
    const form = formState[0];
    const setForm = formState[1];

    /* ─── Editing ─── */
    const editState = useState(null);
    const editingRecord = editState[0];
    const setEditingRecord = editState[1];

    /* ─── UI state ─── */
    const loadState = useState(true);
    const loading = loadState[0];
    const setLoading = loadState[1];

    const savingState = useState(false);
    const saving = savingState[0];
    const setSaving = savingState[1];

    const tabState = useState("service");
    const activeTab = tabState[0];
    const setActiveTab = tabState[1];

    const eligibilityState = useState(false);
    const showEligibility = eligibilityState[0];
    const setShowEligibility = eligibilityState[1];

    /* ─── Fetch data ─── */
    useEffect(function () {
        if (!patient_number || !visit_id) return;
        setLoading(true);
        if (USE_MOCK_DATA) {
            setTimeout(function () {
                setFpRecords(MOCK_FP_RECORDS);
                setLoading(false);
            }, 400);
            return;
        }
        axios.get(config.smsUrl + "/obgyn/family_planing/patient/" + patient_number)
            .then(function (res) {
                const data = res.data || {};
                const records = Array.isArray(data) ? data : (data.records || []);
                setFpRecords(records);
            })
            .catch(function () { })
            .finally(function () { setLoading(false); });
    }, [patient_number, visit_id]);

    /* ─── Form change handler ─── */
    const handleChange = useCallback(function (e) {
        const name = e.target.name;
        const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
        setForm(function (prev) {
            const next = Object.assign({}, prev);
            next[name] = value;

            /* Auto-calculate renewal date when method or provision_date changes */
            if (name === "method_provided" || name === "provision_date") {
                const method = name === "method_provided" ? value : prev.method_provided;
                const provDate = name === "provision_date" ? value : prev.provision_date;
                next.expected_renewal_date = calculateRenewalDate(provDate, method);
            }

            return next;
        });
    }, []);

    /* ─── Side effect toggle ─── */
    const toggleSideEffect = useCallback(function (effectValue) {
        setForm(function (prev) {
            const current = prev.side_effects || [];
            const idx = current.indexOf(effectValue);
            let next;
            if (effectValue === "none") {
                next = ["none"];
            } else if (idx >= 0) {
                next = current.filter(function (c) { return c !== effectValue; });
            } else {
                next = current.filter(function (c) { return c !== "none"; }).concat([effectValue]);
            }
            return Object.assign({}, prev, { side_effects: next });
        });
    }, []);

    /* ─── Contraindication toggle ─── */
    const toggleContraindication = useCallback(function (ci) {
        setForm(function (prev) {
            const current = prev.contraindications_noted || [];
            const idx = current.indexOf(ci);
            let next;
            if (idx >= 0) {
                next = current.filter(function (c) { return c !== ci; });
            } else {
                next = current.concat([ci]);
            }
            return Object.assign({}, prev, { contraindications_noted: next });
        });
    }, []);

    /* ─── Method selection handler ─── */
    const selectMethod = useCallback(function (methodValue) {
        setForm(function (prev) {
            const renewalDate = calculateRenewalDate(prev.provision_date, methodValue);
            return Object.assign({}, prev, {
                method_provided: methodValue,
                expected_renewal_date: renewalDate,
                contraindications_noted: [],
            });
        });
    }, []);

    /* ─── Active method ─── */
    const activeMethod = useMemo(function () {
        const active = fpRecords.filter(function (r) { return r.status === "active"; });
        if (active.length === 0) return null;
        /* Most recent active record */
        return active.sort(function (a, b) {
            return new Date(b.provision_date).getTime() - new Date(a.provision_date).getTime();
        })[0];
    }, [fpRecords]);

    /* ─── Renewal alerts ─── */
    const renewalAlerts = useMemo(function () {
        const alerts = [];
        const today = new Date();
        fpRecords.forEach(function (r) {
            if (r.status !== "active" || !r.expected_renewal_date) return;
            const renewalDate = new Date(r.expected_renewal_date);
            const diffMs = renewalDate.getTime() - today.getTime();
            const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

            if (diffDays < 0) {
                alerts.push({
                    type: "critical",
                    title: "Method Expired",
                    detail: getMethodLabel(r.method_provided) + " expired " + Math.abs(diffDays) + " day(s) ago",
                    action: "Schedule renewal or method switch appointment immediately.",
                    record: r,
                });
            } else if (diffDays <= 30) {
                alerts.push({
                    type: "warning",
                    title: "Renewal Due Soon",
                    detail: getMethodLabel(r.method_provided) + " due for renewal in " + diffDays + " day(s)",
                    action: "Schedule renewal appointment. Counsel on continuation or switching.",
                    record: r,
                });
            } else if (diffDays <= 90) {
                alerts.push({
                    type: "info",
                    title: "Upcoming Renewal",
                    detail: getMethodLabel(r.method_provided) + " renewal in " + diffDays + " day(s) (" + r.expected_renewal_date + ")",
                    action: "Plan ahead for method renewal or review.",
                    record: r,
                });
            }
        });
        return alerts;
    }, [fpRecords]);

    /* ─── BP alerts ─── */
    const bpAlerts = useMemo(function () {
        const alerts = [];
        const sys = parseFloat(form.bp_systolic);
        const dia = parseFloat(form.bp_diastolic);
        const selectedMethod = FP_METHODS.find(function (m) { return m.value === form.method_provided; });

        if (selectedMethod && selectedMethod.requiresBP) {
            if (sys >= 160 || dia >= 100) {
                alerts.push({
                    type: "critical",
                    title: "Severe Hypertension — Contraindication",
                    detail: "BP " + sys + "/" + dia + " mmHg — WHO MEC Category 4 for combined hormonal methods",
                    action: "Do NOT provide COC/CIC. Consider progestin-only or non-hormonal methods.",
                });
            } else if (sys >= 140 || dia >= 90) {
                alerts.push({
                    type: "warning",
                    title: "Elevated Blood Pressure",
                    detail: "BP " + sys + "/" + dia + " mmHg — WHO MEC Category 3 for combined hormonal methods",
                    action: "COC not recommended. Consider progestin-only methods, IUD, or barrier methods.",
                });
            }
        }

        if (!sys && !dia && selectedMethod && selectedMethod.requiresBP) {
            alerts.push({
                type: "info",
                title: "BP Check Required",
                detail: "Blood pressure measurement is required before providing " + selectedMethod.label,
                action: "Please measure and record blood pressure.",
            });
        }

        return alerts;
    }, [form.bp_systolic, form.bp_diastolic, form.method_provided]);

    /* ─── Method eligibility info for current method ─── */
    const currentEligibility = useMemo(function () {
        if (!form.method_provided) return null;
        const mec = MEC_CATEGORIES[form.method_provided];
        if (!mec) return { contraindications: [], precautions: [] };
        return mec;
    }, [form.method_provided]);

    /* ─── Method mix summary ─── */
    const methodMixSummary = useMemo(function () {
        const mix = {};
        FP_METHOD_CATEGORIES.forEach(function (cat) {
            mix[cat.value] = { label: cat.label, icon: cat.icon, color: cat.color, count: 0, methods: {} };
        });
        fpRecords.forEach(function (r) {
            if (r.status !== "active") return;
            const cat = getMethodCategory(r.method_provided);
            if (mix[cat]) {
                mix[cat].count += 1;
                const label = getMethodLabel(r.method_provided);
                mix[cat].methods[label] = (mix[cat].methods[label] || 0) + 1;
            }
        });
        return mix;
    }, [fpRecords]);

    /* ─── Start edit ─── */
    const startEditRecord = useCallback(function (record) {
        setEditingRecord(record);
        setForm(Object.assign({}, INITIAL_FP_FORM, record));
        setActiveTab("service");
    }, []);

    /* ─── Reset form ─── */
    const resetForm = useCallback(function () {
        setEditingRecord(null);
        setForm(Object.assign({}, INITIAL_FP_FORM));
    }, []);

    /* ─── Snackbar helper ─── */
    const showSnackbar = useCallback(function (type, message) {
        if (!actions || !actions.snackbarActions) return;
        const snack = actions.snackbarActions;
        if (typeof snack.snackbarOpen === "function") {
            return snack.snackbarOpen(type, message);
        }
        if (type === "success" && typeof snack.snackSuccess === "function") {
            return snack.snackSuccess(message);
        }
        if (type === "error" && typeof snack.snackError === "function") {
            return snack.snackError(message);
        }
        if (type === "warning" && typeof snack.snackWarning === "function") {
            return snack.snackWarning(message);
        }
        if (type === "info" && typeof snack.snackInfo === "function") {
            return snack.snackInfo(message);
        }
        if (type === "success" && typeof snack.success === "function") {
            return snack.success(message);
        }
        if (type === "error" && typeof snack.error === "function") {
            return snack.error(message);
        }
    }, [actions]);

    /* ─── Save FP record ─── */
    const saveFpRecord = useCallback(function () {
        if (!form.client_type) {
            showSnackbar("error", "Please select the client type");
            return;
        }
        if (!form.method_provided) {
            showSnackbar("error", "Please select the FP method provided");
            return;
        }
        if (!form.provision_date) {
            showSnackbar("error", "Please enter the provision date");
            return;
        }

        /* BP check for hormonal methods */
        const methodInfo = FP_METHODS.find(function (m) { return m.value === form.method_provided; });
        if (methodInfo && methodInfo.requiresBP && !form.bp_systolic && !form.bp_diastolic) {
            showSnackbar("error", "Blood pressure check is required for " + methodInfo.label);
            return;
        }

        if (!form.medical_eligibility_checked) {
            showSnackbar("error", "Please confirm medical eligibility has been checked");
            return;
        }

        setSaving(true);
        const payload = Object.assign({}, form, {
            patient_number: patient_number,
            visit_id: visit_id,
        });

        if (USE_MOCK_DATA) {
            setTimeout(function () {
                if (editingRecord) {
                    const updated = Object.assign({}, editingRecord, payload);
                    const updatedRecords = fpRecords.map(function (r) {
                        return r.id === editingRecord.id ? updated : r;
                    });
                    setFpRecords(updatedRecords);
                    showSnackbar("success", "FP record updated (mock)");
                } else {
                    const saved = Object.assign({}, payload, {
                        id: Date.now(),
                        provided_by: "Current User",
                        created_at: new Date().toISOString(),
                    });
                    setFpRecords(function (prev) { return prev.concat([saved]); });
                    showSnackbar("success", "FP record saved (mock)");
                }
                resetForm();
                setSaving(false);
            }, 300);
            return;
        }

        const request = editingRecord
            ? axios.put(config.smsUrl + "/obgyn/family_planing/" + editingRecord.id, payload)
            : axios.post(config.smsUrl + "/obgyn/family_planing", payload);

        request
            .then(function (res) {
                const data = res.data || payload;
                if (editingRecord) {
                    setFpRecords(function (prev) {
                        return prev.map(function (r) { return r.id === editingRecord.id ? data : r; });
                    });
                } else {
                    setFpRecords(function (prev) { return prev.concat([data]); });
                }
                showSnackbar("success",
                    editingRecord ? "FP record updated" : "FP record saved");
                resetForm();
            })
            .catch(function (err) {
                const msg = (err.response && err.response.data && err.response.data.message) ||
                    "Failed to save FP record";
                showSnackbar("error", msg);
            })
            .finally(function () { setSaving(false); });
    }, [form, patient_number, visit_id, editingRecord, fpRecords, actions, resetForm]);

    /* ─── Discontinue method ─── */
    const discontinueMethod = useCallback(function (recordId, reason) {
        if (USE_MOCK_DATA) {
            setFpRecords(function (prev) {
                return prev.map(function (r) {
                    if (r.id === recordId) {
                        return Object.assign({}, r, { status: "discontinued", discontinuation_reason: reason });
                    }
                    return r;
                });
            });
            showSnackbar("success", "Method discontinued (mock)");
            return;
        }

        axios.put(config.smsUrl + "/obgyn/family_planing/" + recordId, { status: "discontinued", discontinuation_reason: reason })
            .then(function () {
                setFpRecords(function (prev) {
                    return prev.map(function (r) {
                        if (r.id === recordId) {
                            return Object.assign({}, r, { status: "discontinued", discontinuation_reason: reason });
                        }
                        return r;
                    });
                });
                showSnackbar("success", "Method discontinued");
            })
            .catch(function () {
                showSnackbar("error", "Failed to discontinue method");
            });
    }, [actions]);

    /* ─── Summary stats ─── */
    const summary = useMemo(function () {
        return {
            totalRecords: fpRecords.length,
            activeRecords: fpRecords.filter(function (r) { return r.status === "active"; }).length,
            discontinuedRecords: fpRecords.filter(function (r) { return r.status === "discontinued"; }).length,
            switchedRecords: fpRecords.filter(function (r) { return r.status === "switched"; }).length,
            hasActiveMethod: !!activeMethod,
            currentMethodLabel: activeMethod ? getMethodLabel(activeMethod.method_provided) : "None",
        };
    }, [fpRecords, activeMethod]);

    return {
        /* Constants */
        CLIENT_TYPES: CLIENT_TYPES,
        FP_METHODS: FP_METHODS,
        FP_METHOD_CATEGORIES: FP_METHOD_CATEGORIES,
        SIDE_EFFECT_OPTIONS: SIDE_EFFECT_OPTIONS,
        DISCONTINUATION_REASONS: DISCONTINUATION_REASONS,
        FP_STATUS_OPTIONS: FP_STATUS_OPTIONS,
        MEC_CATEGORIES: MEC_CATEGORIES,
        /* Data */
        fpRecords: fpRecords,
        activeMethod: activeMethod,
        /* Form */
        form: form,
        setForm: setForm,
        handleChange: handleChange,
        toggleSideEffect: toggleSideEffect,
        toggleContraindication: toggleContraindication,
        selectMethod: selectMethod,
        editingRecord: editingRecord,
        /* Computed */
        renewalAlerts: renewalAlerts,
        bpAlerts: bpAlerts,
        currentEligibility: currentEligibility,
        methodMixSummary: methodMixSummary,
        summary: summary,
        /* Actions */
        saveFpRecord: saveFpRecord,
        startEditRecord: startEditRecord,
        resetForm: resetForm,
        discontinueMethod: discontinueMethod,
        /* UI */
        loading: loading,
        saving: saving,
        activeTab: activeTab,
        setActiveTab: setActiveTab,
        showEligibility: showEligibility,
        setShowEligibility: setShowEligibility,
        /* Helpers */
        getMethodLabel: getMethodLabel,
        getMethodCategory: getMethodCategory,
        calculateRenewalDate: calculateRenewalDate,
    };
};
